export { html } from 'lit';
import { paramCase } from 'param-case';
import { createMakeTestId } from '@bdc-libs/trinity.dom-helpers';
import { property } from 'lit/decorators.js';

/**
 * Returns true if one or more properties changed.
 *
 * @param me `this` - the lit element instance.
 * @param changedProps changed properties
 * @param propName property name or names
 * @example
 * update(changedProperties) {
 *   if (hasChange(this, changedProperties, 'showSpinner')) {
 *     // Do something
 *   }
 *   if (hasChange(this, changedProperties, ['themeId', 'showSpinner'])) {
 *     // Do something
 *   }
 * }
 */ const hasChange = (// eslint-disable-next-line @typescript-eslint/no-explicit-any
me, changedProps, propName)=>(Array.isArray(propName) ? propName : [
        propName
    ]).some((n)=>changedProps.has(n) && changedProps.get(n) !== me[n]);

/**
 * @license
 * Copyright 2017 Google LLC
 * SPDX-License-Identifier: BSD-3-Clause
 */ /*
 * IMPORTANT: For compatibility with tsickle and the Closure JS compiler, all
 * property decorators (but not class decorators) in this file that have
 * an @ExportDecoratedItems annotation must be defined as a regular function,
 * not an arrow function.
 */ const legacyCustomElement = (tagName, clazz)=>{
    // eslint-disable-next-line no-unused-expressions
    !window.customElements.get(tagName) ? window.customElements.define(tagName, clazz) : null;
    // Cast as any because TS doesn't recognize the return type as being a
    // subtype of the decorated class when clazz is typed as
    // `Constructor<HTMLElement>` for some reason.
    // `Constructor<HTMLElement>` is helpful to make sure the decorator is
    // applied to elements however.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return clazz;
};
const standardCustomElement = (tagName, descriptor)=>{
    const { kind, elements } = descriptor;
    return {
        kind,
        elements,
        // This callback is called once the class is otherwise fully defined
        finisher (clazz) {
            // eslint-disable-next-line no-unused-expressions
            !window.customElements.get(tagName) ? window.customElements.define(tagName, clazz) : null;
        }
    };
};
/**
 * Class decorator factory that defines the decorated class as a custom element.
 *
 * ```
 * @customElement('my-element')
 * class MyElement extends LitElement {
 *   render() {
 *     return html``;
 *   }
 * }
 * ```
 * @category Decorator
 * @param tagName The tag name of the custom element to define.
 */ const customElement = (tagName)=>(classOrDescriptor)=>typeof classOrDescriptor === 'function' ? legacyCustomElement(tagName, classOrDescriptor) : standardCustomElement(tagName, classOrDescriptor);

class NamespaceProvider {
    get namespace() {
        return `${this.__namespace}`;
    }
    set namespace(namespace) {
        this.__namespace = namespace;
    }
    constructor(){
        this.__namespace = '';
    }
}
const namespaceProvider = new NamespaceProvider();

const getDynamicNamespaceTag = (namespace, tagName)=>{
    return namespace ? `${namespace}-${tagName}` : tagName;
};
const defineDynamicTagNamespace = (namespace)=>{
    if (namespace) {
        namespaceProvider.namespace = namespace;
    }
};
const defineDynamicTagNameScopedElement = (customElement, klass)=>{
    var _customElement_constructor;
    const dynamicTagName = getDynamicNamespaceTag(((_customElement_constructor = customElement.constructor) == null ? void 0 : _customElement_constructor.namespace) || '', klass.tagName);
    try {
        if (!customElement.registry.get(dynamicTagName)) {
            customElement.defineScopedElement(dynamicTagName, klass);
        }
    } catch (err) {
        // Clone a new constructor based on the original constructor,
        let NewClazz = class NewClazz extends klass {
        };
        // Make the class name identical to the original constructor's name.
        Reflect.defineProperty(NewClazz, 'name', {
            value: klass.name
        });
        // Use the cloned class to register a custom element for the given name.
        customElements.define(dynamicTagName, NewClazz);
    }
};

const getFullname = (namespace, tagName)=>paramCase([
        namespace,
        tagName
    ].filter(Boolean).join('-'));
const defineCustomElement = (fullname, clazz)=>{
    if (!customElements.get(fullname)) {
        try {
            //
            // Some library which either overrides or re-implements customElements tends to error out
            // when duplicate custom element constructors are found (e.g. JSDOM will throw error when
            // duplicate constructors are going to be defined in the custom element registry).
            //
            // However, in order to make namespaced custom elements work, we need to support the use
            // cases where multiple tag names can be defined using one constructor. For example, both
            // inbox-tri-button and tri-button point to the same TriButton constructor.
            //
            // To work around this conflict between our requirement and library constraints, we come up
            // with a solution where errored-out cases will be caught, and a cloned constructor will be
            // used to define a custom element. The cloned constructor will have a different refenrence
            // than the original constructor's, thus circumventing the 3rd-party library constraints.
            //
            customElements.define(fullname, clazz);
        } catch (err) {
            // Clone a new constructor based on the original constructor,
            let NewClazz = class NewClazz extends clazz {
            };
            // Make the class name identical to the original constructor's name.
            Reflect.defineProperty(NewClazz, 'name', {
                value: clazz.name
            });
            // Use the cloned class to register a custom element for the given name.
            customElements.define(fullname, NewClazz);
        }
    }
};
/**
 * Defines custom elements prefixed with namespace.
 *
 * @example
 *
 * defineCustomElements('inbox', [TriButton, TriDrawer]);
 * defineCustomElements('inbox', {
 *   'tri-button': TriButton,
 *   'tri-drawer': TriDrawer
 * });
 */ const defineCustomElements = (namespace, elementClasses)=>{
    // Get namespace prefix tag and store for components to retrieve when scoping internal elements for OverlayItemMixin
    defineDynamicTagNamespace(namespace);
    if (Array.isArray(elementClasses)) {
        elementClasses.forEach((clazz)=>{
            var _clazz_tagName;
            defineCustomElement(// eslint-disable-next-line @typescript-eslint/no-explicit-any
            getFullname(namespace, (_clazz_tagName = clazz.tagName) != null ? _clazz_tagName : clazz.name), clazz);
        });
    } else {
        Object.entries(elementClasses).forEach(([tagName, clazz])=>{
            defineCustomElement(getFullname(namespace, tagName), clazz);
        });
    }
};

/* eslint-disable no-param-reassign */ // @watch decorator
//
// Runs when an observed property changes, e.g. @property or @state, but before the component updates.
//
// To wait for an update to complete after a change occurs, use `await this.updateComplete` in the handler. To start
// watching after the initial update/render, use `{ waitUntilFirstUpdate: true }` or `this.hasUpdated` in the handler.
//
// Usage:
//
//  @watch('propName')
//  handlePropChange(oldValue, newValue) {
//    ...
//  }
//
function watch(propName, options) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (protoOrDescriptor, name)=>{
        const { update } = protoOrDescriptor;
        options = {
            waitUntilFirstUpdate: false,
            ...options
        };
        protoOrDescriptor.update = function overridingUpdate(// eslint-disable-next-line @typescript-eslint/no-explicit-any
        changedProps) {
            if (changedProps.has(propName)) {
                const oldValue = changedProps.get(propName);
                const newValue = this[propName];
                if (oldValue !== newValue) {
                    if (!(options == null ? void 0 : options.waitUntilFirstUpdate) || this.hasUpdated) {
                        this[name].call(this, oldValue, newValue);
                    }
                }
            }
            update.call(this, changedProps);
        };
    };
}

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for(var i = decorators.length - 1; i >= 0; i--)if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}
typeof SuppressedError === "function" ? SuppressedError : function _SuppressedError(error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

/* eslint-disable max-classes-per-file */ //
/**
 * A mixin that introduces data-testid support to the provided class
 */ const WithTestId = (SuperClass)=>{
    let LitElementWithTestId = class LitElementWithTestId extends SuperClass {
        update(changedProperties) {
            if (!this.testId) {
                // Default dataTestId if not supplied
                this.testId = this._getDefaultHostTestId();
            }
            if (hasChange(this, changedProperties, 'testId')) {
                // Create a new _makeTestId function whenever dataTestId changes
                this._makeTestId = createMakeTestId(this.testId);
            }
            super.update(changedProperties);
        }
        constructor(...args){
            super(...args);
            // eslint-disable-next-line class-methods-use-this
            this._makeTestId = ()=>'';
        }
    };
    __decorate([
        property({
            attribute: 'data-testid',
            type: String,
            reflect: true
        })
    ], LitElementWithTestId.prototype, "testId", void 0);
    return LitElementWithTestId;
};

export { NamespaceProvider, WithTestId, customElement, defineCustomElement, defineCustomElements, defineDynamicTagNameScopedElement, defineDynamicTagNamespace, getDynamicNamespaceTag, getFullname, hasChange, namespaceProvider, watch };
